import React, { useRef, useState } from 'react';
import { Upload, FolderOpen, X, Music, Film } from 'lucide-react';
import { uploadFileToBackend } from '../services/backendService';
import { UserAssetsModal } from './wizard/UserAssetsModal';

export const MAX_REFERENCE_BYTES = 20 * 1024 * 1024;

const detectType = (mime, url) => {
    if (mime?.startsWith('video/')) return 'video';
    if (mime?.startsWith('audio/')) return 'audio';
    return /\.(mp4|mov|webm|mkv|avi)(\?|$)/i.test(url || '') ? 'video' : 'audio';
};

export const validateReferenceFile = (file) => {
    if (!file) return 'No file selected.';
    if (!/^(audio|video)\//.test(file.type || '')) return 'Please choose an audio or video file.';
    if (file.size > MAX_REFERENCE_BYTES) return `File is ${(file.size / 1048576).toFixed(1)} MB — the limit is 20 MB.`;
    return null;
};

export const ReferenceMediaPicker = ({ category, label, value, onChange, disabled }) => {
    const fileRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState(null);
    const [showAssets, setShowAssets] = useState(false);

    const handleFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        const err = validateReferenceFile(file);
        if (err) { setError(err); return; }
        setError(null);
        setUploading(true);
        try {
            const url = await uploadFileToBackend(file);
            onChange({ url, type: detectType(file.type, file.name), fileName: file.name, mimeType: file.type, source: 'upload' });
        } catch (ex) {
            setError(`Upload failed: ${ex.message}`);
        } finally {
            setUploading(false);
        }
    };

    const handleAsset = (url, asset) => {
        setShowAssets(false);
        const mime = asset?.mime_type || asset?.content_type || null;
        onChange({ url, type: detectType(mime, url), fileName: asset?.name || null, mimeType: mime, source: 'user-assets' });
    };

    return (
        <div className="mt-2" data-testid={`ref-picker-${category}`}>
            <input ref={fileRef} type="file" accept="audio/*,video/*" className="hidden" onChange={handleFile} data-testid={`ref-file-input-${category}`} />
            {value?.url ? (
                <div className="flex items-center gap-2 p-2 rounded-md bg-emerald-500/10 border border-emerald-500/30">
                    {value.type === 'video' ? <Film className="w-4 h-4 text-emerald-300 shrink-0" /> : <Music className="w-4 h-4 text-emerald-300 shrink-0" />}
                    <div className="flex-1 min-w-0">
                        <p className="text-[11px] text-emerald-200 truncate" data-testid={`ref-file-name-${category}`}>{value.fileName || `${label} reference`}</p>
                        {value.type === 'video'
                            ? <video src={value.url} controls className="w-full max-h-32 mt-1 rounded" data-testid={`ref-preview-${category}`} />
                            : <audio src={value.url} controls className="w-full h-8 mt-1" data-testid={`ref-preview-${category}`} />}
                    </div>
                    <button type="button" onClick={() => onChange(null)} disabled={disabled}
                        className="p-1 rounded-full hover:bg-red-500/20 text-red-300 self-start" title="Remove reference"
                        data-testid={`ref-remove-${category}`}><X className="w-4 h-4" /></button>
                </div>
            ) : (
                <div className="flex gap-2">
                    <button type="button" onClick={() => fileRef.current?.click()} disabled={disabled || uploading}
                        className="flex-1 px-2 py-1.5 text-xs rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-gray-200 flex items-center justify-center gap-1.5 disabled:opacity-50 transition-colors"
                        data-testid={`ref-upload-${category}`}>
                        {uploading ? <span className="w-3 h-3 border border-white/40 border-t-white rounded-full animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                        {uploading ? 'Uploading…' : 'Upload audio/video'}
                    </button>
                    <button type="button" onClick={() => setShowAssets(true)} disabled={disabled || uploading}
                        className="flex-1 px-2 py-1.5 text-xs rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-gray-200 flex items-center justify-center gap-1.5 disabled:opacity-50 transition-colors"
                        data-testid={`ref-user-assets-${category}`}>
                        <FolderOpen className="w-3.5 h-3.5" /> User assets
                    </button>
                </div>
            )}
            <p className="text-[10px] text-gray-500 mt-1">Optional reference for {label}, max 20 MB.</p>
            {error && <p className="text-[11px] text-red-400 mt-1" data-testid={`ref-error-${category}`}>{error}</p>}
            {showAssets && <UserAssetsModal activeTab="media" onSelect={handleAsset} onClose={() => setShowAssets(false)} />}
        </div>
    );
};
